import { createFileRoute } from '@tanstack/react-router'

import { useEffect, useState } from 'react';
import { Clipboard, Eye, EyeOff, Plus, Settings, Users } from 'lucide-react';
import { EditableConfigField } from '@/components/ui/EditableConfigField';
import { EditableBooleanDropdown } from '@/components/ui/EditableBooleanDropdown';
import { EditableLSPField } from '@/components/ui/EditableLSPField';
import { RatecardConfigField } from '@/components/features/ratecard/ratecard-field';
import { TimePicker } from '@/components/ui/TimePicker';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '@/store';
import { useShallow } from 'zustand/shallow';
import { Api, copyTextToClipboard } from '@/lib/utils';
import { AccountInfo } from '@/lib/interfaces';
import { AddUser } from '@/components/features/profile/add-user';
import { AddAccountDialog } from '@/components/features/profile/add-account';
import Tooltip from '@/components/ui/tooltip';
import { toast } from 'sonner';

export const Route = createFileRoute('/_auth/manage')({
    component: RouteComponent,
})

function RouteComponent() {
    const [showApiKey, setShowApiKey] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const { accountId, token, email, role, apiKey, setPage } = useStore(useShallow(state => ({
        token: state.token,
        accountId: state.selectedAccount.id,
        email: state.email,
        role: state.selectedAccount.role,
        apiKey: state.apiKey,
        setPage: state.setPage
    })))

    useEffect(() => {
        setPage('Manage')
    }, [])

    // Query for account users and config
    const getAccountUsers = useQuery({
        queryKey: ['getAccountUsers', accountId, token],
        queryFn: () => Api<AccountInfo>('/webui/get_account_info', 'POST', { account_id: accountId, mail_id: email || '' }, { token }),
        staleTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false
    });

    // Config state type
    type ConfigState = {
        whitelistedLsps: string;
        blacklistedLsps: string;
        maxRadiusKm: string;
        maxDeliveryFee: string;
        maxOrderAmount: string;
        orderWeight: string;
        codEnabled: boolean;
        rtoRequired: boolean;
        p0Lsps: string;
        p1Lsps: string;
        platformFee: string;
        sendDropCode: boolean;
        timings: string;
    };

    const [config, setConfig] = useState<ConfigState>({
        whitelistedLsps: '',
        blacklistedLsps: '',
        maxRadiusKm: '',
        maxDeliveryFee: '',
        maxOrderAmount: '',
        orderWeight: '',
        codEnabled: false,
        rtoRequired: false,
        p0Lsps: '',
        p1Lsps: '',
        platformFee: '',
        sendDropCode: false,
        timings: ''
    });

    useEffect(() => {
        if (getAccountUsers.data) {
            const loadedConfig: ConfigState = {
                whitelistedLsps: (getAccountUsers.data.whitelisted_lsps || []).join(","),
                blacklistedLsps: (getAccountUsers.data.blacklisted_lsps || []).join(","),
                maxRadiusKm: String(getAccountUsers.data.max_delivery_radius_km ?? ''),
                maxDeliveryFee: String(getAccountUsers.data.max_delivery_fee ?? ''),
                maxOrderAmount: String(getAccountUsers.data.max_order_amount ?? ''),
                orderWeight: String(getAccountUsers.data.max_order_weight ?? ''),
                codEnabled: !!getAccountUsers.data.cod_enabled,
                rtoRequired: !!getAccountUsers.data.default_rto_requirement,
                p0Lsps: (getAccountUsers.data.first_proiority_lsps || []).join(","),
                p1Lsps: (getAccountUsers.data.second_priority_lsps || []).join(","),
                platformFee: String(getAccountUsers.data.platform_fee ?? ''),
                sendDropCode: !!getAccountUsers.data.send_drop_otp_to_customer,
                timings: getAccountUsers.data.timings || ''
            };
            setConfig(loadedConfig);
        }
    }, [getAccountUsers.data]);

    const filteredUsers = (getAccountUsers.data?.users || []).filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phoneNumber.includes(searchTerm) ||
        (user.mailId && user.mailId.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return <div className="absolute left-1 right-1 top-15 bottom-1 overflow-auto space-y-6 py-5 px-3 lg:px-30 md:px-10">
        {/* Create Account Button */}
        {/super_admin/.test(role) ? <Card className='p-4'>
            <div className="flex items-center text-left">
                <AddAccountDialog>
                    <div className="bg-blue-500 text-white p-2 rounded-full mr-4 cursor-pointer">
                        <Plus size={24} />
                    </div>
                </AddAccountDialog>
                <div>
                    <h2 className="font-medium">Create Account</h2>
                    <p className="text-gray-500 text-sm">Create new account</p>
                </div>
            </div>
        </Card> : null}

        {/* Users Section */}
        <Card>
            <div className="px-5 py-1 flex justify-between items-center">
                <div className="items-center gap-2 hidden sm:flex">
                    <Users className="text-gray-500" size={20} />
                    <h2 className="font-medium">Users</h2>
                </div>
                <div className="flex space-x-3 items-end">
                    <Input label={'Search User'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    <AddUser onSuccess={() => getAccountUsers.refetch()}>
                        <div className="bg-blue-500 text-white p-2 rounded-full items-center justify-center flex w-10 h-10 mb-1 cursor-pointer">
                            <Plus size={20} />
                        </div>
                    </AddUser>
                </div>
            </div>

            {/* Users Table */}
            <div className="overflow-auto max-h-96">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="py-3 px-4 text-left font-medium text-gray-600">Phone</th>
                            <th className="py-3 px-4 text-left font-medium text-gray-600">Name</th>
                            <th className="py-3 px-4 text-left font-medium text-gray-600">Email</th>
                            <th className="py-3 px-4 text-left font-medium text-gray-600">Role</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {filteredUsers.map(user => (
                            <tr key={user.username} className="hover:bg-gray-50 text-sm">
                                <td className="py-3 px-4">{user.phoneNumber}</td>
                                <td className="py-3 px-4">{user.username}</td>
                                <td className="py-3 px-4">{user.mailId || '-'}</td>
                                <td className="py-3 px-4">
                                    <span className="bg-blue-100 text-blue-800 py-1 px-3 rounded-full text-sm font-medium">
                                        {user.role}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>

        {email?.endsWith('prorouting.in') && /super_admin/.test(role || '') && (
  <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 my-4 rounded">
    <b>Note:</b> Changes to editable configs can take up to 1 hour to reflect. Please wait for this duration and do not update again if already done.
  </div>
)}

        {/* Editable Configs Card - Only for prorouting.in users */}
        {/admin/.test(role || '') && getAccountUsers.data && email?.endsWith('prorouting.in') && /super_admin/.test(role || '') ? (
          <Card className="mb-6">
            <div className="p-4 flex items-center">
                <Settings className="text-gray-500 mr-2" size={20} />
                <h2 className="font-medium text-lg">Editable Configs</h2>
            </div>
            <div className="divide-y divide-gray-200 *:flex *:items-center *:py-4 *:px-6 *:hover:bg-gray-50 text-sm gap-10 *:gap-2">
                <EditableLSPField
                  label="Whitelisted LSPs"
                  value={config.whitelistedLsps}
                  onSave={async (newValue) => {
                      // Remove spaces and ensure only comma-separated URIs
                      const sanitized = newValue.split(',').map(x => x.trim()).filter(Boolean).join(',');
                      const payload = { account_id: accountId, key_name: 'whitelistedLsps', key_value: sanitized, user_email: email };
                      const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                      if (resp.status === 1) {
                        toast.success('Config updated!');
                      } else {
                        toast.error('Failed to update config. Kindly contact tech support');
                      }
                      getAccountUsers.refetch();
                  }}
                  onChange={val => setConfig(c => ({ ...c, whitelistedLsps: val }))}
                  placeholder="uri1,uri2,uri3..."
                  isEditable={
                    (email?.endsWith('prorouting.in')) || (/super_admin/.test(role || ''))
                  }
                  
                />

                <EditableLSPField
                  label="Blacklisted LSPs"
                  value={config.blacklistedLsps}
                  onSave={async (newValue) => {
                    // Remove spaces and ensure only comma-separated URIs
                      const sanitized = newValue.split(',').map(x => x.trim()).filter(Boolean).join(',');
                      const payload = { account_id: accountId, key_name: 'blacklistedLsps', key_value: sanitized, user_email: email };
                      const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
              if (resp.status === 1) {
                toast.success('Config updated!');
              } else {
                toast.error(resp.message || 'Failed to update config. Kindly contact tech support');
              }
                      getAccountUsers.refetch();
                  }}
                  onChange={val => setConfig(c => ({ ...c, blacklistedLsps: val }))}
                  placeholder="uri1,uri2,uri3"
                  isEditable={
                    (email?.endsWith('prorouting.in')) || (/super_admin/.test(role || ''))
                  }
                />

                {(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableConfigField
                    label="Max Radius (km)"
                    value={config.maxRadiusKm}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                        toast.error('Please enter a valid number');
                        return;
                        }
                        const payload = { account_id: accountId, key_name: 'maxRadiusKm', key_value: newValue, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                toast.success('Max Radius updated!');
                getAccountUsers.refetch();
            }}
            onChange={val => setConfig(c => ({ ...c, maxRadiusKm: val }))}
          />
        ) : (
          <div className="flex items-center gap-2">
            <p className="flex-1 font-medium text-gray-500">Max Radius (km)</p>
            <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
              {config.maxRadiusKm || <span className="text-gray-400">(empty)</span>}
            </span>
          </div>
        )}
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableConfigField
                    label="Max Delivery Fee"
                    value={config.maxDeliveryFee}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                            toast.error('Please enter a valid number');
                            return;
                        }
                        const payload = { account_id: accountId, key_name: 'maxDeliveryFee', key_value: newValue, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Max Delivery Fee updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, maxDeliveryFee: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Max Delivery Fee</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.maxDeliveryFee || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableConfigField
                    label="Max Order Amount"
                    value={config.maxOrderAmount}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                            toast.error('Please enter a valid number');
                            return;
                        }
                        const payload = { account_id: accountId, key_name: 'maxOrderAmount', key_value: newValue, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Max Order Amount updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, maxOrderAmount: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Max Order Amount</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.maxOrderAmount || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableConfigField
                    label="Order Weight"
                    value={config.orderWeight}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                            toast.error('Please enter a valid number');
                            return;
                        }
                        const payload = { account_id: accountId, key_name: 'orderWeight', key_value: newValue, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Order Weight updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, orderWeight: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Order Weight</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.orderWeight || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableBooleanDropdown
                    label="COD Enabled"
                    value={config.codEnabled}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, key_name: 'codEnabled', key_value: newValue ? 1 : 0, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('COD Enabled updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, codEnabled: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">COD Enabled</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.codEnabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableBooleanDropdown
                    label="RTO Required"
                    value={config.rtoRequired}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, key_name: 'rtoRequired', key_value: newValue ? 1 : 0, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('RTO Required updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, rtoRequired: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">RTO Required</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.rtoRequired ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                <EditableLSPField
                  label="First Priority LSPs"
                  value={config.p0Lsps}
                  onSave={async (newValue) => {
                      const sanitized = newValue.split(',').map(x => x.trim()).filter(Boolean).join(',');
                      const payload = { account_id: accountId, key_name: 'p0Lsps', key_value: sanitized, user_email: email };
                      const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
              if (resp.status === 1) {
                toast.success('Config updated!');
              } else {
                toast.error('Failed to update config. Kindly contact tech support');
              }
                      toast.success('First Priority LSPs updated!');
                      getAccountUsers.refetch();
                  }}
                  onChange={val => setConfig(c => ({ ...c, p0Lsps: val }))}
                  placeholder="lsp1, lsp2, ..."
                  isEditable={
                    (email?.endsWith('prorouting.in')) || (/super_admin/.test(role || ''))
                  }
                />

                <EditableLSPField
                  label="Second Priority LSPs"
                  value={config.p1Lsps}
                  onSave={async (newValue) => {
                    const sanitized = newValue.split(',').map(x => x.trim()).filter(Boolean).join(',');
                      const payload = { account_id: accountId, key_name: 'p1Lsps', key_value: sanitized, user_email: email };
                      const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
              if (resp.status === 1) {
                toast.success('Config updated!');
              } else {
                toast.error('Failed to update config. Kindly contact tech support');
              }
                      toast.success('P1 LSPs updated!');
                      getAccountUsers.refetch();
                  }}
                  onChange={val => setConfig(c => ({ ...c, p1Lsps: val }))}
                  placeholder="lsp1, lsp2, ..."
                  isEditable={
                    (email?.endsWith('prorouting.in')) || (/super_admin/.test(role || ''))
                  }
                />

{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableConfigField
                    label="Platform Fee"
                    value={config.platformFee}
                    onSave={async (newValue) => {
                        if (!/^\d+(\.\d{1})?$/.test(newValue)) {
                        toast.error('Please enter a valid number (up to 1 decimal place)');
                        return;
                        }
                        const payload = { account_id: accountId, key_name: 'platformFee', key_value: newValue, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error(resp.message || 'Failed to update config. Kindly contact tech support');
                }
                        toast.success('Platform Fee updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, platformFee: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Platform Fee</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.platformFee || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
<RatecardConfigField isEditable={!!(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || ''))} />
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <EditableBooleanDropdown
                    label="Send Drop Code"
                    value={config.sendDropCode}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, key_name: 'sendDropCode', key_value: newValue ? 1 : 0, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error(resp.message || 'Failed to update config. Kindly contact tech support');
                }
                        toast.success('Send Drop Code updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, sendDropCode: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Send Drop Code</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.sendDropCode ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in')) || (/super_admin/.test(role || '')) ? (
                  <TimePicker
                    label="Timings"
                    value={config.timings}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, key_name: 'timings', key_value: newValue, user_email: email };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Timings updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, timings: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Timings</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.timings || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
            </div>
        </Card>
        ) : null}

        {/* Account Configs Card - Only for non-prorouting.in users - merged view */}
        {/admin/.test(role || '') && getAccountUsers.data && !(/super_admin/.test(role || '')) ? (
          <Card className="mb-6">
            <div className="p-4 flex items-center">
                <Settings className="text-gray-500 mr-2" size={20} />
                <h2 className="font-medium text-lg">Account Configs</h2>
            </div>
            <div className="divide-y divide-gray-200 *:flex *:items-center *:py-4 *:px-6 *:hover:bg-gray-50 text-sm gap-10 *:gap-2">

                {(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableConfigField
                    label="Max Radius (km)"
                    value={config.maxRadiusKm}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                        toast.error('Please enter a valid number');
                        return;
                        }
                        const payload = { account_id: accountId, maxRadiusKm: newValue };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                toast.success('Max Radius updated!');
                getAccountUsers.refetch();
            }}
            onChange={val => setConfig(c => ({ ...c, maxRadiusKm: val }))}
          />
        ) : (
          <div className="flex items-center gap-2">
            <p className="flex-1 font-medium text-gray-500">Max Radius (km)</p>
            <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
              {config.maxRadiusKm || <span className="text-gray-400">(empty)</span>}
            </span>
          </div>
        )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableConfigField
                    label="Max Delivery Fee"
                    value={config.maxDeliveryFee}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                            toast.error('Please enter a valid number');
                            return;
                        }
                        const payload = { account_id: accountId, maxDeliveryFee: newValue };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Max Delivery Fee updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, maxDeliveryFee: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Max Delivery Fee</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.maxDeliveryFee || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableConfigField
                    label="Max Order Amount"
                    value={config.maxOrderAmount}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                            toast.error('Please enter a valid number');
                            return;
                        }
                        const payload = { account_id: accountId, maxOrderAmount: newValue };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Max Order Amount updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, maxOrderAmount: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Max Order Amount</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.maxOrderAmount || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableConfigField
                    label="Order Weight"
                    value={config.orderWeight}
                    onSave={async (newValue) => {
                        if (!/^[0-9]+$/.test(newValue)) {
                            toast.error('Please enter a valid number');
                            return;
                        }
                        const payload = { account_id: accountId, orderWeight: newValue };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Order Weight updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, orderWeight: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Order Weight</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.orderWeight || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableBooleanDropdown
                    label="COD Enabled"
                    value={config.codEnabled}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, codEnabled: newValue ? 1 : 0 };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('COD Enabled updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, codEnabled: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">COD Enabled</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.codEnabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableBooleanDropdown
                    label="RTO Required"
                    value={config.rtoRequired}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, rtoRequired: newValue ? 1 : 0 };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('RTO Required updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, rtoRequired: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">RTO Required</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.rtoRequired ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableConfigField
                    label="Platform Fee"
                    value={config.platformFee}
                    onSave={async (newValue) => {
                        if (!/^\d+(\.\d{1})?$/.test(newValue)) {
                        toast.error('Please enter a valid number (up to 1 decimal place)');
                        return;
                        }
                        const payload = { account_id: accountId, platformFee: newValue };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error(resp.message || 'Failed to update config. Kindly contact tech support');
                }
                        toast.success('Platform Fee updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, platformFee: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Platform Fee</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.platformFee || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}
<RatecardConfigField isEditable={!!(email?.endsWith('prorouting.in') && (/super_admin/.test(role || '')))} />
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <EditableBooleanDropdown
                    label="Send Drop Code"
                    value={config.sendDropCode}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, sendDropCode: newValue ? 1 : 0 };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error(resp.message || 'Failed to update config. Kindly contact tech support');
                }
                        toast.success('Send Drop Code updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, sendDropCode: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Send Drop Code</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.sendDropCode ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
{(email?.endsWith('prorouting.in') && (/super_admin/.test(role || ''))) ? (
                  <TimePicker
                    label="Timings"
                    value={config.timings}
                    onSave={async (newValue) => {
                        const payload = { account_id: accountId, timings: newValue };
                        const resp = await Api('/webui/update_account_info', 'POST', payload, { token }) as any;
                if (resp.status === 1) {
                  toast.success('Config updated!');
                } else {
                  toast.error('Failed to update config. Kindly contact tech support');
                }
                        toast.success('Timings updated!');
                        getAccountUsers.refetch();
                    }}
                    onChange={val => setConfig(c => ({ ...c, timings: val }))}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium text-gray-500">Timings</p>
                    <span className="flex-1 md:text-left bg-gray-100 rounded px-2 py-1 min-h-[36px] flex items-center break-all whitespace-pre-wrap" title={typeof config.whitelistedLsps === 'string' ? config.whitelistedLsps : undefined}>
                      {config.timings || <span className="text-gray-400">(empty)</span>}
                    </span>
                  </div>
                )}

                <div>
                    <p className="flex-1 font-medium text-gray-500">GST Number</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.gst_number}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Default Order Category</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.default_order_category}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Credit Limit</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.credit_limit}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Track Callback URL</p>
                    <input className="flex-1  md:text-left outline-none border-none" value={getAccountUsers.data.track_callback_url} readOnly />
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Auto Select Mode</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.auto_select_mode}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Default RTO Requirement</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.default_rto_requirement}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Callback URL</p>
                    <input className="flex-1  md:text-left outline-none border-none" value={getAccountUsers.data.callback_url} readOnly />
                </div>
                <div className='flex flex-col items-start lg:flex-row'>
                    <p className="flex-1 font-medium text-gray-500 w-full">API Key</p>
                    <div className='flex gap-6 items-center flex-1 w-full mt-3'>
                        <div className="font-mono">
                            <input value={showApiKey?apiKey:apiKey.substring(0, 5) + '* '.repeat(15) + apiKey.substring(apiKey.length - 5)} readOnly className='outline-none border-none lg:w-96 text-xs sm:text-sm' />
                        </div>
                        <div onClick={() => setShowApiKey(prev => !prev)} className='cursor-pointer'>
                            {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                        </div>
                        <Tooltip title="Copy API Key">
                            <Clipboard size={18} className='cursor-pointer' onClick={async () => {
                                const didCopy = await copyTextToClipboard(apiKey)
                                if (didCopy) {
                                    toast.success('API Key copied to clipboard')
                                } else {
                                    toast.error('Failed to copy API Key')
                                }
                            }} />
                        </Tooltip>
                    </div>
                </div>
            </div>
        </Card>
        ) : null}

        {/* Other Configs Card - Only for prorouting.in users */}
        {/super_admin/.test(role || '') && getAccountUsers.data && email?.endsWith('prorouting.in') ? (
<Card>
            <div className="p-4 flex items-center">
                <Settings className="text-gray-500 mr-2" size={20} />
                <h2 className="font-medium text-lg">Other Configs</h2>
            </div>
            <div className="divide-y divide-gray-200 *:flex *:items-center *:py-4 *:px-6 *:hover:bg-gray-50 text-sm gap-10 *:gap-2">
                <div>
                    <p className="flex-1 font-medium text-gray-500">GST Number</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.gst_number}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Default Order Category</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.default_order_category}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Credit Limit</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.credit_limit}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Track Callback URL</p>
                    <input className="flex-1  md:text-left outline-none border-none" value={getAccountUsers.data.track_callback_url}/>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Auto Select Mode</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.auto_select_mode}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Default RTO Requirement</p>
                    <p className="flex-1  md:text-left">{getAccountUsers.data.default_rto_requirement}</p>
                </div>
                <div>
                    <p className="flex-1 font-medium text-gray-500">Callback URL</p>
                    <input className="flex-1  md:text-left outline-none border-none" value={getAccountUsers.data.callback_url}/>
                </div>
                <div className='flex flex-col items-start lg:flex-row'>
                    <p className="flex-1 font-medium text-gray-500 w-full">API Key</p>
                    <div className='flex gap-6 items-center flex-1 w-full mt-3'>
                        <div className="font-mono">
                            <input value={showApiKey?apiKey:apiKey.substring(0, 5) + '* '.repeat(15) + apiKey.substring(apiKey.length - 5)} readOnly className='outline-none border-none lg:w-96 text-xs sm:text-sm' />
                        </div>
                        <div onClick={() => setShowApiKey(prev => !prev)} className='cursor-pointer'>
                            {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                        </div>
                        <Tooltip title="Copy API Key">
                            <Clipboard size={18} className='cursor-pointer' onClick={async () => {
                                const didCopy = await copyTextToClipboard(apiKey)
                                if (didCopy) {
                                    toast.success('API Key copied to clipboard')
                                } else {
                                    toast.error('Failed to copy API Key')
                                }
                            }} />
                        </Tooltip>
                    </div>
                </div>
            </div>
        </Card>
        ) : null}

    </div>
}
